import useAbortableData from "@hooks/useAbortableData";
import http from "@core/fetch";
import apiConfig from "@apiConfig";
import { components } from "@apiSchema";
import { PermissionLevelEnum, PermissionSection } from "@models/PermissionSection";

type FoodProfileRelatedContentResponse = components["schemas"]["FoodProfileRelatedContentModel"];
type ApiRelatedProfile = components["schemas"]["FoodProfileRelatedContentItemModel"];
type ApiRelatedLinks = components["schemas"]["MacOrInstantChartModelListPermissionWrapper"];

export interface FoodProfileRelatedProfile {
    id: number;
    name: string;
}

export interface FoodProfileRelatedLink {
    id: number;
    name: string;
    /** `null` when the BE sends no URL (e.g. the user has no access): the item is still listed, as a locked row. */
    link: string | null;
}

/** Instant charts / MACs come in the BE permission wrapper: the level decides whether the sidebar locks them. */
export interface FoodProfileRelatedLinks {
    permissionLevel: PermissionLevelEnum;
    items: FoodProfileRelatedLink[];
}

export interface FoodProfileRelatedContent {
    imageUrl: string | null;
    categoryProfiles: FoodProfileRelatedProfile[];
    foodProfiles: FoodProfileRelatedProfile[];
    instantCharts: FoodProfileRelatedLinks;
    macs: FoodProfileRelatedLinks;
}

/** No wrapper (loading, error or old response): nothing to list, and an unknown level never reads as access. */
const EMPTY_LINKS: FoodProfileRelatedLinks = { permissionLevel: PermissionLevelEnum.None, items: [] };

const REQUEST_TYPE: keyof typeof apiConfig = "get food profile related content";

/** Module-level so `useData` gets a stable default between renders. */
const EMPTY_RELATED_CONTENT: FoodProfileRelatedContent = {
    imageUrl: null,
    categoryProfiles: [],
    foodProfiles: [],
    instantCharts: EMPTY_LINKS,
    macs: EMPTY_LINKS,
};

const toText = (value?: string | null) => value?.trim() || null;

function mapProfiles(items?: ApiRelatedProfile[] | null): FoodProfileRelatedProfile[] {
    return (items ?? []).flatMap(({ id, name }) => {
        const text = toText(name);
        return typeof id === "number" && text ? [{ id, name: text }] : [];
    });
}

function mapLinks(wrapper?: ApiRelatedLinks | null): FoodProfileRelatedLinks {
    if (!wrapper) return EMPTY_LINKS;
    const items = (wrapper.data ?? []).flatMap(({ id, name, link }) => {
        const text = toText(name);
        return typeof id === "number" && text ? [{ id, name: text, link: toText(link) }] : [];
    });
    return { permissionLevel: PermissionSection.getPermissionLevel(wrapper.permissionLevel), items };
}

/**
 * Normalises the API payload and drops items that can't be rendered (no name). Charts/MACs without a link are kept
 * (the BE omits it without access), so the sidebar can list them as locked rows; it decides whether one is shown.
 * Item limits, ranking and excluding the current profile are owned by the BE, so lists are kept as returned.
 */
export function mapFoodProfileRelatedContent(
    response?: FoodProfileRelatedContentResponse | null,
): FoodProfileRelatedContent {
    if (!response) return EMPTY_RELATED_CONTENT;
    return {
        imageUrl: toText(response.imageUrl),
        categoryProfiles: mapProfiles(response.categoryProfiles),
        foodProfiles: mapProfiles(response.foodProfiles),
        instantCharts: mapLinks(response.instantCharts),
        macs: mapLinks(response.macs),
    };
}

async function getFoodProfileRelatedContent(word: string, signal: AbortSignal): Promise<FoodProfileRelatedContent> {
    const response: FoodProfileRelatedContentResponse = await http.get(REQUEST_TYPE, { word }, { signal });
    return mapFoodProfileRelatedContent(response);
}

/** Related content for the Food Profile sidebar. Empty on no word, 204, 400 or an unparseable response. */
export default function useFoodProfileRelatedContent(word: string) {
    return useAbortableData(EMPTY_RELATED_CONTENT, getFoodProfileRelatedContent, word, REQUEST_TYPE);
}
